import { useState, useRef, useEffect, useCallback } from "react";
import { Loader2, Send, Sparkles } from "lucide-react";
import moment from "moment";

import { Button } from "@/modern/components/ui/button";
import { MarkdownRenderer } from "@/modern/components/ui/markdown-renderer";
import { cn } from "@/modern/utils";
import { sendChatMessage } from "@/network/network";

import type {
  ChatMessage,
  SendMessageRequest,
  SendMessageResponse,
} from "@/modern/components/chatbox/types";

import { useNotificationScope } from "../hooks/useNotificationScope";
import type {
  MappedNotificationCounts,
  NotificationSummaryModel,
} from "../types";
import { buildUpdatesAskUiContext } from "../utils/askUiContext";

const SUGGESTED_PROMPTS = [
  "What failed since I last checked?",
  "Which updates need my attention?",
  "Show the schedule notifications.",
  "What did Swivu complete in the background?",
];

export function NotificationAskView({
  prose,
  summary,
  mappedCounts,
}: {
  prose: string;
  summary: Pick<NotificationSummaryModel, "total" | "unread" | "notable">;
  mappedCounts: Pick<MappedNotificationCounts, "categories">;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { boardId, isAccountView } = useNotificationScope();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  }, [input]);

  const buildAskPayload = useCallback(
    (query: string, currentSessionId?: string): SendMessageRequest => {
      const payload: SendMessageRequest = {
        query,
        local_datetime: moment().format("YYYY-MM-DDTHH:mm:ssZ"),
        ui_context: buildUpdatesAskUiContext({ summary, mappedCounts }),
      };
      if (currentSessionId) {
        payload.session_id = currentSessionId;
      }
      if (!isAccountView && boardId != null) {
        payload.board_id = boardId;
      }
      return payload;
    },
    [boardId, isAccountView, mappedCounts, summary],
  );

  const handleSend = async (text?: string) => {
    const message = (text ?? input).trim();
    if (!message || isLoading) return;

    setInput("");
    const userMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: message,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setIsLoading(true);

    try {
      const payload = buildAskPayload(message, sessionId);
      const response = await sendChatMessage(payload);
      const data: SendMessageResponse = response.data;

      if (data.session_id) {
        setSessionId(data.session_id);
      }

      const assistantMsg: ChatMessage = {
        id: data.message_id || `resp-${Date.now()}`,
        role: "assistant",
        content: data.answer,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch {
      const errorMsg: ChatMessage = {
        id: `err-${Date.now()}`,
        role: "assistant",
        content: "Sorry, I couldn't process that request. Please try again.",
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col px-5 sm:px-7">
      {/* Scrollable message area */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto py-4">
        {/* Header - always shown */}
        {!hasMessages ? (
          <div className="mx-auto max-w-2xl">
            <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
              <Sparkles className="size-3.5 text-primary" aria-hidden />
              Ask Swivu
            </div>
            <h2 className="text-3xl font-bold tracking-tight">About these updates</h2>
            <p className="mt-2 text-sm text-muted-foreground">{prose}</p>

            {/* Suggested questions */}
            <div className="mt-6 rounded-2xl border border-border/60 bg-muted/20 p-4">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                Suggested questions
              </p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_PROMPTS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => void handleSend(prompt)}
                    className="rounded-full border border-border/60 bg-muted/30 px-3 py-1.5 text-xs text-foreground/80 transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="mx-auto flex max-w-2xl flex-col gap-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "flex gap-3",
                  msg.role === "user" ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-4 py-3 text-sm",
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted/50 text-foreground ring-1 ring-inset ring-border/50",
                  )}
                >
                  {msg.role === "assistant" ? (
                    <MarkdownRenderer content={msg.content} />
                  ) : (
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                  )}
                </div>
              </div>
            ))}
            {isLoading ? (
              <div className="flex gap-3">
                <div className="rounded-2xl bg-muted/50 px-4 py-3 ring-1 ring-inset ring-border/50">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Input area */}
      <div className="shrink-0 border-t border-border/40 pb-4 pt-3">
        <div className="mx-auto flex max-w-2xl items-end gap-2">
          <div className="relative flex-1">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about these updates..."
              rows={1}
              className="flex max-h-[120px] min-h-[44px] w-full resize-none rounded-2xl border border-border/60 bg-muted/20 px-4 py-3 pr-12 text-sm outline-none placeholder:text-muted-foreground/50 focus-visible:ring-1 focus-visible:ring-ring"
            />
            <Button
              type="button"
              size="icon"
              disabled={!input.trim() || isLoading}
              onClick={() => void handleSend()}
              className="absolute bottom-1.5 right-1.5 size-8 rounded-full"
            >
              <Send className="size-3.5" aria-hidden />
            </Button>
          </div>
        </div>
        <p className="mx-auto mt-1.5 max-w-2xl text-center text-[11px] text-muted-foreground/60">
          <kbd className="rounded border border-border/50 bg-muted/40 px-1 py-0.5 text-[10px]">Enter</kbd> to send · <kbd className="rounded border border-border/50 bg-muted/40 px-1 py-0.5 text-[10px]">Shift + Enter</kbd> for new line
        </p>
      </div>
    </div>
  );
}
